import { useState, useRef } from "react";
import { Camera, X, MessageCircle, CheckCircle2, Loader2, Send } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND } from "@/lib/constants";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 3;
const MAX_SIZE = 10 * 1024 * 1024;

const getCookie = (name) => {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
};

const genEventId = () =>
  window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `ev-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/10 focus:border-[#e10600]/60 focus:ring-2 focus:ring-[#e10600]/20 outline-none px-4 py-3.5 text-white placeholder:text-white/30 transition";

const buildLeadFormData = (form, photos, lang, eventId) => {
  const fd = new FormData();
  fd.append("name", form.name.trim());
  fd.append("phone", form.phone.trim());
  fd.append("car", form.car.trim());
  fd.append("description", form.description.trim());
  fd.append("language", lang);
  fd.append("event_id", eventId);
  fd.append("event_source_url", window.location.href);
  fd.append("fbp", getCookie("_fbp") || "");
  fd.append("fbc", getCookie("_fbc") || "");
  photos.forEach((p) => fd.append("photos", p.file));
  return fd;
};

const META_PIXEL_ID = "1023138560083799";

// Normalise phone to digits only (Meta strips +, spaces, dashes anyway).
const normalisePhone = (raw) => (raw || "").replace(/\D/g, "");

// Split full name into first/last for better Meta matching.
const splitName = (full) => {
  const parts = (full || "").trim().toLowerCase().split(/\s+/);
  return { fn: parts[0] || "", ln: parts.slice(1).join(" ") || "" };
};

const fireLeadAnalytics = (eventId, lang, form) => {
  if (window.fbq) {
    const { fn, ln } = splitName(form.name);
    // Re-init pixel with Advanced Matching — values are SHA-256 hashed by Meta SDK automatically.
    window.fbq("init", META_PIXEL_ID, {
      em: "",
      ph: normalisePhone(form.phone),
      fn,
      ln,
      country: "kw",
    });
    // NOTE: Meta "Lead" event intentionally NOT fired — the Lead event name is
    // reserved for a future server-side qualified-lead integration.
  }
  if (window.gtag) window.gtag("event", "generate_lead", { language: lang });
};

const SuccessPanel = ({ q, waLink, onReset }) => (
  <div className="relative flex flex-col items-center text-center py-8" data-testid="quote-success">
    <CheckCircle2 className="w-14 h-14 text-emerald-400 mb-4" />
    <h3 className="font-display font-black text-2xl md:text-3xl">{q.successTitle}</h3>
    <p className="mt-3 text-white/65 max-w-md">{q.successText}</p>
    <a
      href={waLink}
      target="_blank"
      rel="noreferrer"
      className="mt-6 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#25D366] hover:bg-[#1fb957] text-black font-bold transition shadow-xl shadow-emerald-900/30"
      data-testid="quote-whatsapp-continue-btn"
    >
      <MessageCircle className="w-5 h-5" />
      {q.continueWhatsApp}
    </a>
    <button
      onClick={onReset}
      className="mt-4 text-sm text-white/50 hover:text-white underline underline-offset-4 transition"
      data-testid="quote-new-request-btn"
    >
      {q.newRequest}
    </button>
  </div>
);

const PhotoPicker = ({ q, photos, onFiles, onRemove, fileRef }) => (
  <div className="md:col-span-2">
    <label className="block text-sm font-semibold text-white/70 mb-2">{q.photosLabel}</label>
    <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="quote-photo-input" />
    <div className="flex flex-wrap items-center gap-3">
      {photos.map((p, i) => (
        <div key={p.url} className="relative w-20 h-20 rounded-xl overflow-hidden border border-white/15" data-testid={`quote-photo-preview-${i}`}>
          <img src={p.url} alt={`dent ${i + 1}`} className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onRemove(i)}
            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-[#e10600] transition"
            data-testid={`quote-photo-remove-${i}`}
            aria-label="Remove photo"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      {photos.length < MAX_PHOTOS && (
        <button
          type="button"
          onClick={() => fileRef.current && fileRef.current.click()}
          className="w-20 h-20 rounded-xl border border-dashed border-white/25 hover:border-[#e10600]/60 flex flex-col items-center justify-center gap-1 text-white/50 hover:text-white transition"
          data-testid="quote-add-photo-btn"
        >
          <Camera className="w-5 h-5" />
          <span className="text-[10px] font-semibold">{q.addPhotos}</span>
        </button>
      )}
    </div>
    <p className="mt-2 text-xs text-white/40">{q.photosHint}</p>
  </div>
);

const QuoteForm = () => {
  const { t, lang } = useLanguage();
  const q = t.quote;
  const [form, setForm] = useState({ name: "", phone: "", car: "", description: "" });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validateFile = (file, alreadyCount) => {
    if (!file.type.startsWith("image/")) return null;
    if (alreadyCount >= MAX_PHOTOS) return "limit";
    if (file.size > MAX_SIZE) return "limit";
    return "ok";
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setError("");
    setPhotos((prev) => {
      const next = [...prev];
      for (const f of files) {
        const verdict = validateFile(f, next.length);
        if (verdict === null) continue;
        if (verdict === "limit") { setError(q.errorPhotos); break; }
        next.push({ file: f, url: URL.createObjectURL(f) });
      }
      return next;
    });
    e.target.value = "";
  };

  const removePhoto = (i) => {
    URL.revokeObjectURL(photos[i].url);
    setPhotos(photos.filter((_, idx) => idx !== i));
  };

  const waMessage =
    lang === "ar"
      ? `مرحباً دنت إكسبرس! أرسلت طلب تقييم.\nالاسم: ${form.name}\nالسيارة: ${form.car || "-"}\nالضربة: ${form.description || "-"}`
      : `Hi Dent Xpress! I just sent a quote request.\nName: ${form.name}\nCar: ${form.car || "-"}\nDent: ${form.description || "-"}`;
  const waLink = `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent(waMessage)}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setError(q.requiredNote);
      return;
    }
    setSubmitting(true);
    setError("");
    const eventId = genEventId();
    try {
      const res = await fetch(`${API}/api/leads`, {
        method: "POST",
        body: buildLeadFormData(form, photos, lang, eventId),
      });
      if (!res.ok) throw new Error("submit failed");
      fireLeadAnalytics(eventId, lang, form);
      setSubmitted(true);
    } catch (err) {
      setError(q.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    photos.forEach((p) => URL.revokeObjectURL(p.url));
    setPhotos([]);
    setForm({ name: "", phone: "", car: "", description: "" });
    setSubmitted(false);
    setError("");
  };

  return (
    <section id="quote" className="relative py-20 md:py-28" data-testid="quote-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-[11px] tracking-[0.25em] uppercase font-display text-white/70">
            <span className="w-1.5 h-1.5 rounded-full bg-[#e10600]" />
            {q.eyebrow}
          </div>
          <h2 className="mt-4 font-display font-black text-3xl md:text-5xl tracking-tight">{q.title}</h2>
          <p className="mt-4 text-white/65 max-w-2xl mx-auto">{q.subtitle}</p>
        </div>

        <div className="relative rounded-3xl border border-white/10 overflow-hidden bg-gradient-to-br from-[#141414] via-[#0f0f0f] to-[#120505] p-6 md:p-10">
          <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[#e10600]/10 blur-3xl pointer-events-none" />

          {submitted ? (
            <SuccessPanel q={q} waLink={waLink} onReset={resetForm} />
          ) : (
            <form onSubmit={onSubmit} className="relative grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="quote-form">
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">{q.nameLabel} *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  placeholder={q.namePh}
                  className={inputCls}
                  required
                  data-testid="quote-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">{q.phoneLabel} *</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder={q.phonePh}
                  className={inputCls}
                  required
                  dir="ltr"
                  data-testid="quote-phone-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-white/70 mb-2">{q.carLabel}</label>
                <input
                  type="text"
                  value={form.car}
                  onChange={set("car")}
                  placeholder={q.carPh}
                  className={inputCls}
                  data-testid="quote-car-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-white/70 mb-2">{q.descLabel}</label>
                <textarea
                  value={form.description}
                  onChange={set("description")}
                  placeholder={q.descPh}
                  rows={3}
                  className={inputCls}
                  data-testid="quote-desc-input"
                />
              </div>

              <PhotoPicker q={q} photos={photos} onFiles={onFiles} onRemove={removePhoto} fileRef={fileRef} />

              {error && (
                <p className="md:col-span-2 text-sm text-red-400 font-semibold" data-testid="quote-error-text">{error}</p>
              )}

              <div className="md:col-span-2 flex justify-center mt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#e10600] hover:bg-[#c20500] disabled:opacity-60 text-white font-bold transition shadow-xl shadow-red-900/40"
                  data-testid="quote-submit-btn"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  {submitting ? q.submitting : q.submit}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
